/**
 * MessageBody.jsx — presentation-only styling for the text of a chat message.
 *
 * Two distinct treatments (ChatGPT / Claude / Emergent all follow this
 * pattern):
 *   • USER    → compact cyan pill, right-aligned, single-line-ish, no markdown.
 *   • ASSISTANT → no bubble; flowing document text; full markdown (GFM) with
 *     Rammy-tuned typography and a blinking caret while streaming.
 *
 * We keep the outer positioning (justify-end vs justify-start, image / doc /
 * compute chips) in the parent chat.jsx — this file only owns the text block.
 */
import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Copy, Check } from "lucide-react";
import api from "@/lib/api";

/** Blinking caret shown at the end of the currently-streaming assistant reply. */
function StreamingCaret() {
  return (
    <span
      className="inline-block w-[2px] h-[1em] align-[-2px] ml-0.5 bg-[var(--rammy-cyan-dark)] animate-pulse"
      aria-hidden="true"
      data-testid="assistant-streaming-caret"
    />
  );
}

// Hoisted so ReactMarkdown doesn't rebuild the component tree on every render.
const MD_P = (props) => <p className="my-3 first:mt-0 last:mb-0" {...props} />;
const MD_H1 = (props) => <h1 className="font-display text-[22px] leading-tight mt-5 mb-2 first:mt-0" {...props} />;
const MD_H2 = (props) => <h2 className="font-display text-[19px] leading-tight mt-5 mb-2 first:mt-0" {...props} />;
const MD_H3 = (props) => <h3 className="font-medium text-[17px] leading-snug mt-4 mb-1.5 first:mt-0" {...props} />;
const MD_UL = (props) => <ul className="my-3 pl-5 space-y-1.5 list-disc marker:text-[var(--rammy-cyan-dark)]" {...props} />;
const MD_OL = (props) => <ol className="my-3 pl-5 space-y-1.5 list-decimal marker:text-[var(--rammy-cyan-dark)] marker:font-medium" {...props} />;
const MD_LI = (props) => <li className="pl-1 [&>p]:my-0" {...props} />;
const MD_STRONG = (props) => <strong className="font-semibold text-[var(--rammy-text)]" {...props} />;
const MD_EM = (props) => <em className="italic text-[var(--rammy-text)]/85" {...props} />;
const MD_A = (props) => (
  <a
    {...props}
    target="_blank"
    rel="noopener noreferrer"
    className="text-[var(--rammy-cyan-dark)] underline decoration-[var(--rammy-cyan-dark)]/40 underline-offset-2 hover:decoration-[var(--rammy-cyan-dark)]"
  />
);
const MD_BLOCKQUOTE = (props) => (
  <blockquote
    className="my-3 border-l-2 border-[var(--rammy-cyan-dark)]/40 pl-3 italic text-[var(--rammy-text)]/85"
    {...props}
  />
);
const MD_CODE = ({ inline, className, children, ...props }) => {
  if (inline) {
    return (
      <code
        className="rounded-md bg-[var(--rammy-surface-alt)] border border-[var(--rammy-border)] px-1.5 py-0.5 text-[0.88em] font-mono"
        {...props}
      >
        {children}
      </code>
    );
  }
  // Block-level code — extract the raw text once so the copy button can grab
  // it without touching the DOM.
  const raw = Array.isArray(children)
    ? children.map(c => (typeof c === "string" ? c : (c?.props?.children ?? ""))).join("")
    : (typeof children === "string" ? children : (children?.props?.children ?? ""));
  const rawStr = String(raw);
  // A "code block" that's really just a filename ("index.html"), a directory
  // ("/assets/…") or a one-line snippet does NOT deserve the full-width dark
  // treatment — it visually shouts louder than the surrounding prose and
  // makes Rammy feel like a compiler. Downgrade to an inline chip whenever the
  // content is short + few-lined.
  const lineCount = rawStr.split("\n").filter(Boolean).length;
  if (rawStr.trim().length < 60 && lineCount <= 2) {
    return (
      <code
        className="rounded-md bg-[var(--rammy-surface-alt)] border border-[var(--rammy-border)] px-1.5 py-0.5 text-[0.88em] font-mono text-[var(--rammy-text)]"
        data-testid="chat-inline-mini-code"
      >
        {rawStr.trim()}
      </code>
    );
  }
  return <CodeBlock className={className} raw={rawStr}>{children}</CodeBlock>;
};

/** Block code with an unobtrusive copy button that pops "Copied" for 1.4s. */
function CodeBlock({ className, raw, children }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(raw);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      /* clipboard may be blocked in preview iframes — silent fail is fine */
    }
  };
  return (
    <div className="my-3 group relative">
      <button
        type="button"
        onClick={onCopy}
        className="absolute top-2 right-2 z-10 inline-flex items-center gap-1 rounded-md bg-white/10 hover:bg-white/20 text-white/80 hover:text-white text-[11px] px-2 py-1 opacity-0 group-hover:opacity-100 focus:opacity-100 transition"
        aria-label={copied ? "Copied" : "Copy code"}
        data-testid="chat-code-copy"
      >
        {copied
          ? (<><Check size={12}/> Copied</>)
          : (<><Copy  size={12}/> Copy</>)}
      </button>
      <pre className="rounded-xl bg-[var(--rm-ink-900)] text-[#e6f7f2] p-3.5 pr-14 overflow-x-auto text-[13px] leading-relaxed">
        <code className={`font-mono ${className || ""}`}>{children}</code>
      </pre>
    </div>
  );
}
const MD_TABLE = (props) => (
  <div className="my-3 overflow-x-auto rounded-xl border border-[var(--rammy-border)]">
    <table className="w-full text-[14px] border-collapse" {...props} />
  </div>
);
const MD_THEAD = (props) => <thead className="bg-[var(--rammy-surface-alt)] text-[var(--rammy-text)]" {...props} />;
const MD_TH = (props) => <th className="text-left font-medium px-3 py-2 border-b border-[var(--rammy-border)]" {...props} />;
const MD_TD = (props) => <td className="px-3 py-2 border-b border-[var(--rammy-border)] last:border-b-0" {...props} />;
const MD_HR = (props) => <hr className="my-4 border-[var(--rammy-border)]" {...props} />;

const MD_COMPONENTS = {
  p: MD_P, h1: MD_H1, h2: MD_H2, h3: MD_H3,
  ul: MD_UL, ol: MD_OL, li: MD_LI,
  strong: MD_STRONG, em: MD_EM, a: MD_A,
  blockquote: MD_BLOCKQUOTE, code: MD_CODE,
  table: MD_TABLE, thead: MD_THEAD, th: MD_TH, td: MD_TD,
  hr: MD_HR,
};

/** Markdown renderer with Rammy-tuned component overrides.
 *  Every element inherits our warm palette; no purple / neon defaults.
 */
export function AssistantMarkdown({ text }) {
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
      {text}
    </ReactMarkdown>
  );
}

/** Wraps assistant message text with the reading-optimised container. */
export function AssistantBody({ text, streaming, placeholder }) {
  // While streaming with no text yet, show a warm italic hint. The pulsing
  // Rammy orb marker on the outer `.rammy-response` container already carries
  // the heartbeat — a second inline animator would compete with it. This
  // keeps the exchange calm and readable.
  if (streaming && !text) {
    return (
      <div
        className="text-[13.5px] text-[var(--rammy-text-muted)] italic"
        data-testid="assistant-typing"
      >
        {placeholder || "thinking…"}
      </div>
    );
  }
  return (
    <div
      className="rammy-assistant-prose rm-voice"
      data-testid="assistant-body"
    >
      <AssistantMarkdown text={text || ""}/>
      {streaming && <StreamingCaret/>}
    </div>
  );
}

/** User bubble — compact, right-aligned, no markdown (users just type sentences). */
export function UserBody({ text }) {
  return (
    <div
      className="rm-turn-user whitespace-pre-wrap"
      data-testid="user-body"
    >
      {text}
    </div>
  );
}

/** "Copy reply" — small icon-only button rendered next to MessageFeedback.
 *  Copies the plain-text assistant reply to the clipboard. Deliberately no
 *  markdown stripping — power-users tend to want the raw text so they can
 *  paste it into a doc that will re-render the syntax.
 */
export function CopyReplyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
      // Silent DNA auto-learn — user copying an assistant reply is a
      // strong implicit approval signal. Fire-and-forget; backend
      // throttles + skips samples < 200 chars so this is cheap.
      const clean = (text || "").trim();
      if (clean.length >= 200) {
        api.post("/dna/learn", {
          sample: clean.slice(0, 12000),
          source: "approved_reply",
        }).catch(() => { /* silent */ });
      }
    } catch {
      /* clipboard blocked — silent */
    }
  };
  return (
    <button
      type="button"
      onClick={onCopy}
      className="inline-flex items-center gap-1 rounded-full border border-[var(--rammy-border)] bg-[var(--rm-surface)] text-[var(--rammy-text-muted)] hover:text-[var(--rammy-text)] hover:border-[var(--rammy-cyan-dark)]/40 text-[11px] px-2.5 py-1 transition"
      aria-label={copied ? "Reply copied to clipboard" : "Copy reply"}
      data-testid="chat-copy-reply"
    >
      {copied
        ? (<><Check size={12}/> Copied</>)
        : (<><Copy  size={12}/> Copy reply</>)}
    </button>
  );
}
